/*** IMPORTS FROM imports-loader ***/
var define = false;

/*jslint browser:true, todo:true*/
"use strict";

var ko = require("knockout");
var R = require("ramda");

var $ = require("../jquery");

if (require("targetenv").isBrowser) {
    // Provides synthetic 'input' events as 'textchange' events
    // TODO: Remove when IE9 support is officially dropped
    require("jquery-splendid-textchange");
}


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// TextBoxController class
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-------------------------------------------
// Create a new instance of text box controller
// (during "pliTextBox" binding initialization).
//
// Methods called "on...$" (e.g. "onFocus$")
// expect TextBoxController reference to be
// passed via "e.data", not "this" (this is
// the standard way to pass context to event
// handlers when using jQuery's "$.on(...)").
function TextBoxController(element, valueAccessor, allBindings) {
    // define properties
    this.observable = this._verifyNewObservable(valueAccessor());
    this.$input = $(element);
    this.$root = this.$input.closest(".pliControl");
    this.$icon = this.$root.find("i");
    this.$errorText = this.$root.find("b.withError");
    this.customFormat = allBindings.get("pliTextBoxFormat");
    this.eventStream = null;
    this.isFocused = false;
    this._applyChanges = this._applyChanges.bind(this);
    this._applyChangesTimerId = null;
    this._updateDelayInMillisecs = allBindings.get("pliTextBoxUpdateDelay");
    if (typeof this._updateDelayInMillisecs !== "number") {
        this._updateDelayInMillisecs = (this.observable.behaviourData && this.observable.behaviourData.updateDelay);
        if (typeof this._updateDelayInMillisecs !== "number") {
            this._updateDelayInMillisecs = 500;
        }
    }

    // bind to view
    if (this.observable._initBinding(this, allBindings) !== false) { // explicit check for "false", do not replace with falsy
        // handle icon clicks
        this.$icon.on("mousedown", this, this.showSelectionForm$);

        // handle user-initiated changes
        // TODO: `textchange` -> `input` when IE9 support is completely dropped
        this.$input.on("change textchange", this, this.onChange$);

        // mark as focused when user enters
        this.$input.on("focus", this, this.onFocus$);

        // reformat value when user leaves
        this.$input.on("blur", this, this.onBlur$);

        // add support for custom shortcuts
        this.$input.on("keydown", this, this.onKeyDown$);
    }

    // update view with initial observable value
    this.observable._addController(this);
    this.updateView(true); // ignore focus
    this.observable._updateBinding(this, allBindings);

    // set placeholder
    if (element.tagName.toLowerCase() === 'input') {
        var placeholder = (this.observable.behaviourData && this.observable.behaviourData.placeholder)
            || this.$input.attr('data-placeholder');
        if (placeholder) {
            this._setPlaceholder(placeholder);
        }
    }
}


//-------------------------------------------
// Return true when the bound HTML element is
// enabled (when its value can be changed,
// i.e. when it is not disabled or readonly).
TextBoxController.prototype.isEnabled = function textBoxControllerIsEnabled() {
    return !this.$input.is(":disabled, [readonly]");
};


//-------------------------------------------
// Reformat bound HTML element when user leaves
// and mark this controller as not focused.
//
// Expects "e.data" to point to TextBoxController
// (intended for use with "$.on('event', controller, onBlur$)".
TextBoxController.prototype.onBlur$ = function textBoxControllerOnBlur$(e) {
    var controller = e.data;
    controller.isFocused = false;
    controller._applyChanges(true); // skip view update
    controller.observable._updateView();
};


//-------------------------------------------
// Update bound observable (called when
// bound HTML element changes).
//
// Expects "e.data" to point to TextBoxController
// (intended for use with "$.on('event', controller, onChange$)".
TextBoxController.prototype.onChange$ = function textBoxControllerOnChange$(e) {
    var controller = e.data;
    if (controller._updateDelayInMillisecs <= 0) {
        controller._applyChanges(false, true); // do not skip view update, force apply (run now even without timer ID)
    } else {
        if (controller._applyChangesTimerId !== null) { window.clearTimeout(controller._applyChangesTimerId); }
        controller._applyChangesTimerId = window.setTimeout(controller._applyChanges, controller._updateDelayInMillisecs);
    }
};


//-------------------------------------------
// Mark this controller as focused.
//
// Expects "e.data" to point to TextBoxController
// (intended for use with "$.on('event', controller, onFocus$)".
TextBoxController.prototype.onFocus$ = function textBoxControllerOnFocus$(e) {
    var controller = e.data;
    controller._applyChanges(); // will update view if there are pending changes
    controller.isFocused = true;
};


//-------------------------------------------
// Recognize and handle "pliTextBox"
// keyboard shortcuts.
//
// Expects "e.data" to point to TextBoxController
// (intended for use with "$.on('event', controller, onKeyDown$)".
TextBoxController.prototype.onKeyDown$ = function textBoxControllerOnKeyDown$(e) {
    switch (e.which) {
    case 114: // F3 (show context menu)
        return e.data.showSelectionForm$(e);
    }
};


//-------------------------------------------
// If current observable supports
// selection form, show it now.
//
// Expects "e.data" to point to TextBoxController
// (intended for use with "$.on('event', controller, showSelectionForm$)".
TextBoxController.prototype.showSelectionForm$ = function textBoxControllerShowSelectionForm$(e) {
    var controller = e.data;
    if (typeof controller.observable.show === "function" && controller.isEnabled()) {
        controller._applyChanges();
        var result = controller.observable.show({
            onShowDone: controller._showSelectionFormDone,
            onShowDoneContext: controller,
            $input: controller.$input
        });
        if (result === false && this._preventDefault) { // explicit check for "false", do not replace with falsy
            this._preventDefault(e);
        }
        return result;
    }
};


//-------------------------------------------
// Update bound HTML element.
TextBoxController.prototype.updateView = function textBoxControllerUpdateView(ignoreFocus) {
    var o = this.observable;
    var withError = !o.isValid.peek();
    if (withError && !this.$errorText.length) {
        this.$errorText = $("<b class=\"withError\"></b>");
        this.$root.append(this.$errorText);
    }

    this.$errorText.text(o._errorMessage || "");
    this.$root.toggleClass("withError", withError);

    if (ignoreFocus || !this.isFocused) {
        this.updateViewValue();
    }
};


//-------------------------------------------
// Synchronize value of the bound HTML element
// with the current observable value (called
// from "updateView" method).
TextBoxController.prototype.updateViewValue = function textBoxControllerUpdateViewValue() {
    var value = this.observable.format(this.customFormat);
    this.$input.val(value);
};


//-------------------------------------------
// If no change is pending ("_applyChangesTimerId"),
// does nothing (unless "forceApply" is true).
// Also ignores spurious change events (mostly
// happens in IE, we get notified of change but
// the value has not really changed).
//
// If a change is pending, will notify underlying
// observable (using "_loadText") and then update
// view  (if "_loadText" made any changes).
TextBoxController.prototype._applyChanges = function textBoxControllerApplyChanges(skipViewUpdate, forceApply) {
    // cancel pending change requests
    if (this._applyChangesTimerId !== null) {
        window.clearTimeout(this._applyChangesTimerId);
        this._applyChangesTimerId = null;
    } else {
        if (!forceApply) { return; } // if no change request is pending and we are not force-applying, we are done
    }

    // filter out spurious change events
    var targetValue = this.observable.peekValue();
    if (targetValue === null) { targetValue = ""; }
    var inputValueStr = this.$input.val() || "";
    if (inputValueStr === targetValue) { return; }

    // activate and update the linked observable
    this.observable._isActivated = true;
    var updateNeeded = this.observable._loadText(inputValueStr);
    if (updateNeeded && !skipViewUpdate) {
        this.observable._updateView();
    }
};


//-------------------------------------------
// Key that is used to store controller
// object as a property of bound HTML element
// (similar technique is used by built-in
// Knockout bindings, they are using
// "__ko__" prefix).
TextBoxController.prototype._elementKey = "__kopli__textbox";


//-------------------------------------------
// Initialize new "pliTextBox" binding by creating
// a new controller object and storing it as
// a property of the bound element.
//
// This is a "static" method, do not use "this".
TextBoxController.prototype._koInit = function textBoxControllerKoInit(element, valueAccessor, allBindings) {
    element[TextBoxController.prototype._elementKey] = new TextBoxController(element, valueAccessor, allBindings);
};


//-------------------------------------------
// Update value of HTML element bound by
// "pliTextBox" binding (this method will be
// called by Knockout when bound observable
// sends a change notification or when
// the observable itself changes).
//
// This is a "static" method, do not use "this".
TextBoxController.prototype._koUpdate = function textBoxControllerKoUpdate(element, valueAccessor, allBindings) {
    var controller = TextBoxController.prototype._requireController(element);

    var o = valueAccessor();
    if (controller.observable !== o) {
        controller.observable._removeController(controller); // old observable
        controller.observable = controller._verifyNewObservable(o);
        controller.observable._addController(controller); // new observable
        controller.observable._updateBinding(controller, allBindings);
        controller.updateView(true); // ignore focus
    }
};


//-------------------------------------------
// If the current browser is Internet Explorer
// 8 or older, this method will be defined
// and will contain IE-specific code that
// will prevent default for special keys
// (like F3). In other browsers this method
// will be null (not defined).
TextBoxController.prototype._preventDefault = (function createPreventDefault() {
    if ((document.documentMode || 100) > 8) { return null; } // most browsers don't need this hack

    return function iePreventDefault(e) {
        try { e.originalEvent.keyCode = 0; } catch (ignore) {}
    };
}());


//-------------------------------------------
// Get controller associated with the specified
// element. Throws an error if the specified
// element is not associated with a controller.
//
// This is a "static" method, do not use "this".
TextBoxController.prototype._requireController = function textBoxControllerRequireController(element) {
    var controller = (element && element[TextBoxController.prototype._elementKey]);
    if (!controller) { throw new Error("Target element has not been bound using \"pliTextBox\" binding"); }
    return controller;
};


//-------------------------------------------
TextBoxController.prototype._setPlaceholder = function textBoxControllerSetPlaceholder(placeholder) {
    this.$input.attr(
        "placeholder",
        typeof placeholder === "function" ? placeholder() : placeholder
    );
    if (ko.isSubscribable(placeholder)) {
        placeholder.subscribe(R.partial(this.$input.attr, ["placeholder"]), this.$input);
    }
};


//-------------------------------------------
// Callback invoked when the selection form
// shown using "show" method is closed
// (either accepting value or cancelling).
TextBoxController.prototype._showSelectionFormDone = function textBoxControllerShowSelectionFormDone(v) {
    if (v === undefined) {
        return this.observable.peek();
    }

    this.observable.load(v);
    this.observable._updateView(true); // ignore focus
};


//-------------------------------------------
// Make sure the specified observable "o"
// is a valid textbox observable (i.e. is
// itself an observable and has "_loadText"
// and other required methods).
//
// Called when observable object changes
// (not value of the observable, but the
// observable itself; this will happen
// when more complex bindings are used,
// e.g. "dois.current().area" -- every
// time "current" changes, "area" observable
// will change as well, so the same view
// element is now bound to a different
// viewmodel object).
TextBoxController.prototype._verifyNewObservable = function textBoxControllerVerifyNewObservable(o) {
    if (!ko.isWriteableObservable(o)) { throw new Error("Target of \"pliTextBox\" binding must be a writeable observable (did you use \"pli.observable\"?)"); }
    if (!ko.isObservable(o.isValid)) { throw new Error("Target of \"pliTextBox\" binding must have a \"isValid\" observable (did you use \"pli.observable\"?)"); }
    if (typeof o._addController !== "function") { throw new Error("Target of \"pliTextBox\" binding must have a \"_addController\" method (did you use \"pli.observable\"?)"); }
    if (typeof o._loadText !== "function") { throw new Error("Target of \"pliTextBox\" binding must have a \"_loadText\" method (did you use \"pli.observable\"?)"); }
    if (typeof o._initBinding !== "function") { throw new Error("Target of \"pliTextBox\" binding must have a \"_initBinding\" method (did you use \"pli.observable\"?)"); }
    if (typeof o._removeController !== "function") { throw new Error("Target of \"pliTextBox\" binding must have a \"_removeController\" method (did you use \"pli.observable\"?)"); }
    if (typeof o._updateView !== "function") { throw new Error("Target of \"pliTextBox\" binding must have a \"_updateView\" method (did you use \"pli.observable\"?)"); }
    return o;
};


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Public
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

module.exports = TextBoxController;




//////////////////
// WEBPACK FOOTER
// ./~/pli/forms-knockoutcontrols/TextBoxController.js
// module id = 17
// module chunks = 49